import {
    Autocomplete,
    AutocompleteChangeDetails,
    AutocompleteRenderGetTagProps,
    Box,
    Checkbox,
    Chip,
    ListItem,
    SxProps,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { headingFontBold, bodyFont, vars } from '../../../../Theme';
import Icon from '@mdi/react';
import { mdiClose, mdiAlertCircle } from '@mdi/js';
import { ContactPersonKey as ContactPersonEmailAddressKey } from '../../../../redux/project/documents/sliceProjectShareEmail';
import { useTranslation } from 'react-i18next';
import Message from './Message';
import { IProjectUserEmailContact } from '../../../../redux/project/sliceProjectUserList';
import { useSelector } from 'react-redux';
import { IAppReduxStore } from '../../../../redux/store';
import { v4 as uuidv4 } from 'uuid';
import { matchesInOrder } from '../../../../util/DataUtils';
import { isEmail } from '../../../../util/StringUtils';
import { genObjectId } from '../../../../util/HashUtils';

const textFieldSx: SxProps = {
    input: { ...headingFontBold, py: 1 },

    '& .MuiOutlinedInput-root': {
        padding: '6.5px 14px',
        '&.Mui-focused fieldset': {
            border: `2px solid ${vars.black}`,
        },
    },
    '& .MuiFormHelperText-root': {
        marginLeft: 'unset',
    },

    '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
        py: '4px',
    },
};

const getChipSx = (exists: boolean): SxProps => ({
    ...headingFontBold,
    backgroundColor: exists ? vars.tagChipBlue : vars.concrete,
    color: vars.white,
    fontSize: '14px',
    fontWeight: 500,
    padding: '4px 6px',
    lineHeight: '120%',

    '.MuiChip-deleteIcon': {
        color: vars.white,
    },
});

export default ({
    emails,
    setEmails,
    field,
    placeholder,
    isDisabled = false,
    hasError,
    errorMessage,
}: IInputEmailProps) => {
    const { t } = useTranslation('translations', { keyPrefix: 'dialogParts' });

    const contacts = useSelector((_store: IAppReduxStore) => _store.projectUserList.userEmails);

    const [inputValue, setInputValue] = useState<string>('');

    const updateEmails = (newEmails: string[]) => {
        setEmails({ field: field, value: newEmails });
    };

    const validateEmail = (email: string): boolean => {
        if (!email) return false;

        if (email === ContactPersonEmailAddressKey) return true;

        return isEmail(email);
    };

    const handleDeleteEmail = (deleteEmail: string) => {
        updateEmails(emails.filter(e => e !== deleteEmail));
    };

    const handleOnBlur = () => {
        if (validateEmail(inputValue)) {
            updateEmails([...emails, inputValue]);
        }
        setInputValue('');
    };

    const handleOnKeyDown = (ev: React.KeyboardEvent) => {
        if ([' ', ';', ',', 'Enter'].includes(ev.key)) {
            ev.preventDefault(); // Prevent default behavior of separator keys

            if (validateEmail(inputValue)) {
                updateEmails([...emails, inputValue]);
                setInputValue('');
            }
        }
    };

    const handleChange = (
        values: IProjectUserEmailContact[] | string[],
        details: AutocompleteChangeDetails<string | IProjectUserEmailContact>,
    ) => {
        const newMail =
            typeof details.option === 'string' ? [details.option] : details.option.email;

        if (newMail.every(validateEmail)) {
            updateEmails([
                ...new Set(
                    values.flatMap(option => (typeof option === 'string' ? option : option.email)),
                ),
            ]);
        }
    };

    const onChipClick = (value: string) => {
        handleDeleteEmail(value);
        setInputValue(value);
    };

    const getOptionLabel = (option: IProjectUserEmailContact) =>
        option.name + (option.company ? ` - ${option.company}` : '');

    const emailExists = (emailValue: string): boolean => {
        const email = emailValue.toLowerCase().trim();
        return contacts.some(contact =>
            contact.email.some(
                contactEmail => contactEmail.toLowerCase().trim() === email
            ),
        );
    };

    const renderTags = (
        values: IProjectUserEmailContact[] | string[],
        getTagProps: AutocompleteRenderGetTagProps,
    ) => {
        const filteredEmails = new Set<string>();

        return values.flatMap((emailOption, emailOptionIndex) =>
            emailOption.email.map((email, index) => {
                if (!filteredEmails.has(email)) {
                    filteredEmails.add(email);

                    let chipLabel = '';

                    if (emailOption[index] === ContactPersonEmailAddressKey) {
                        chipLabel = chipLabel[t('contactPersonMailAddress')];
                    } else if (emailOption?.email.length > 1) {
                        chipLabel = email;
                    } else {
                        chipLabel = emailOption.name;
                    }

                    return (
                        <Chip
                            variant='filled'
                            label={chipLabel}
                            {...getTagProps({ index: emailOptionIndex })}
                            onDelete={() => handleDeleteEmail(email)}
                            size='small'
                            sx={getChipSx(emailExists(email))}
                            key={uuidv4()}
                            deleteIcon={<Icon path={mdiClose} size={'16px'} />}
                            onClick={() => onChipClick(email)}
                        />
                    );
                }
            }),
        );
    };

    const filteredEmailContacts = emails
        .flatMap(email => {
            const existingEmailContact = contacts.filter(
                obj => obj.email.includes(email) && matchesInOrder(obj.email, emails),
            );

            return existingEmailContact?.length > 0
                ? existingEmailContact
                : { name: email, email: [email] };
        })
        .filter(
            (email, index, self) =>
                index === self.findIndex(e => e.name.toLowerCase() === email.name.toLowerCase()),
        );

    const onCheckboxClick = (contact: IProjectUserEmailContact, isChecked: boolean) => {
        const newValue = isChecked
            ? emails.concat(contact.email)
            : emails.filter(x => !contact.email.some(y => x === y));

        updateEmails(newValue);
    };

    return (
        <Autocomplete<IProjectUserEmailContact, true, true, true>
            multiple
            disabled={isDisabled}
            id='tags-filled'
            options={contacts}
            size='small'
            disableClearable
            value={filteredEmailContacts}
            inputValue={inputValue}
            onInputChange={(_ev, value) => setInputValue(value)}
            onKeyDown={handleOnKeyDown}
            onBlur={handleOnBlur}
            filterOptions={(options, state) => {
                const searchText = state.inputValue.toLowerCase();
                return options.filter(opt =>
                    JSON.stringify(opt).toLowerCase().includes(searchText),
                );
            }}
            onChange={(
                _ev,
                value: IProjectUserEmailContact[] | string[],
                _reason,
                details: AutocompleteChangeDetails<string | IProjectUserEmailContact>,
            ) => handleChange(value, details)}
            freeSolo
            disableCloseOnSelect
            getOptionLabel={getOptionLabel}
            renderOption={(props, option) => (
                <ListItem {...props} key={genObjectId(option)} sx={{ padding: '12px 16px' }}>
                    <Checkbox
                        sx={{ marginRight: '12px', padding: '0px' }}
                        checked={emails.some(email => option.email.includes(email))}
                        onClick={ev => {
                            // prevent <autocomplete> component to interprete click
                            ev.stopPropagation();
                            onCheckboxClick(option, ev.target['checked']);
                        }}
                    />
                    <Box sx={{ overflow: 'auto' }}>
                        <Typography
                            sx={{
                                fontSize: '14px',
                                fontWeight: 'bold',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {getOptionLabel(option)}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: '12px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                marginRight: '12px',
                            }}
                        >
                            {option.email.join(', ')}
                        </Typography>
                    </Box>
                    {option.email.length > 1 && (
                        <Tooltip
                            title={option.email.map(email => {
                                return (
                                    <span key={uuidv4()} style={{ display: 'block' }}>
                                        {email}
                                    </span>
                                );
                            })}
                        >
                            <Chip
                                variant='filled'
                                label={option.email.length}
                                size='small'
                                sx={{
                                    ...headingFontBold,
                                    marginLeft: 'auto',
                                    color: vars.white,
                                    backgroundColor: vars.brandDarkBlue,
                                }}
                            />
                        </Tooltip>
                    )}
                </ListItem>
            )}
            renderTags={(values, getTagProps) => renderTags(values, getTagProps)}
            renderInput={params => (
                <TextField
                    {...params}
                    sx={textFieldSx}
                    placeholder={emails.length < 1 ? placeholder : ''}
                    error={hasError}
                    helperText={
                        hasError && (
                            <Message
                                text={errorMessage}
                                icon={mdiAlertCircle}
                                iconSize={0.75}
                                color={vars.errorDialogRed}
                                typographySx={{
                                    fontWeight: 400,
                                    fontSize: '12px',
                                    ...bodyFont,
                                }}
                                wrapperSx={{
                                    border: 'none',
                                    p: 0,
                                    m: 0,
                                    gap: 0.5,
                                }}
                            />
                        )
                    }
                />
            )}
        />
    );
};

interface IInputEmailProps {
    emails: string[];
    setEmails: ({ field, value }: { field: string; value: string[] }) => void;
    field: string;
    placeholder: string;
    isDisabled?: boolean;
    hasError?: boolean;
    errorMessage?: string;
}
